import { sortGuides } from '@rhg/utils/guides';
import {
    getClinicalProgramState,
    getProgramType,
    ProgramState,
} from '@rhg/utils/programs';
import { sortTools } from '@rhg/utils/tools';
import {
    getWorkflowCurrentStep,
    getWorkflowPackClass,
} from '@rhg/utils/workflows';
import { useMemo } from 'react';
import {
    ProgramContextProps,
    ProgramContextState,
    ToolGuideMap,
} from './types';

export function useProgramContext({
    program,
    packClass: providedPackClass,
    packProgram,
    workflow,
    recommendation,
    isFetched = true,
}: ProgramContextProps): ProgramContextState {
    const numSessions = useMemo(
        () => program?.guides?.filter((guide) => !guide.isTrailer).length ?? 0,
        [program?.guides]
    );

    const programState = useMemo(() => {
        if (!program?.isClinical) {
            return !program?.completed &&
                program?.guides?.some((g) => g.completed)
                ? ProgramState.InProgress
                : ProgramState.Unspecified;
        }
        return getClinicalProgramState(workflow);
    }, [workflow, program]);
    const programType = useMemo(
        () => getProgramType(program, workflow),
        [program, workflow]
    );

    const toolsByGuide = useMemo(
        () =>
            program?.tools
                ?.filter((tool) => !!tool?.assets)
                ?.sort(sortTools)
                .reduce<ToolGuideMap>(
                    (toolGuideMap, tool) => ({
                        ...toolGuideMap,
                        [tool.guideId]: toolGuideMap[tool.guideId]
                            ? [...toolGuideMap[tool.guideId], tool]
                            : [tool],
                    }),
                    {}
                ) ?? {},
        [program?.tools]
    );

    const orderedSessions = useMemo(
        () => program?.guides?.sort(sortGuides) ?? [],
        [program?.guides]
    );

    const firstSession = useMemo(
        () => orderedSessions.find((session) => !session.isTrailer),
        [orderedSessions]
    );

    const numSessionsCompleted = useMemo(
        () =>
            program?.guides?.filter(
                (guide) => !guide.isTrailer && guide.completed
            ).length,
        [program?.guides]
    );

    const currentSession = useMemo(
        () =>
            orderedSessions?.find(
                ({ completed, isTrailer }) =>
                    (programState !== ProgramState.InProgress || !isTrailer) &&
                    !completed
            ),
        [orderedSessions, programState]
    );

    const progress = useMemo(
        () =>
            programState === ProgramState.InProgress && numSessions
                ? (numSessionsCompleted ?? 0) / numSessions
                : undefined,
        [numSessions, numSessionsCompleted, programState]
    );

    const currentWorkflowStep = useMemo(
        () => getWorkflowCurrentStep(workflow),
        [workflow]
    );

    const pollWorkflowSteps = useMemo(
        () =>
            workflow?.steps.filter(
                (step) =>
                    step.stepDefn.type === 'poll' && step.stepDefn.data.display
            ),
        [workflow?.steps]
    );

    const packClass = useMemo(
        () =>
            providedPackClass
                ? providedPackClass
                : workflow
                ? getWorkflowPackClass(workflow)
                : undefined,
        [providedPackClass, workflow]
    );

    return {
        program,
        workflow,
        progress,
        currentSession,
        currentWorkflowStep,
        numSessionsCompleted,
        orderedSessions,
        pollWorkflowSteps,
        packClass,
        packProgram,
        programType,
        programState,
        numSessions,
        firstSession,
        toolsByGuide,
        recommendation,
        isFetched: isFetched,
    };
}
